import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PaperAirplaneIcon, 
  TrashIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  LanguageIcon,
  PencilIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface GrammarCorrection {
  mistake: string;
  correction: string;
  explanation: string;
}

interface Corrections {
  hasErrors: boolean;
  feedback: GrammarCorrection[];
}

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  corrections?: Corrections;
}

interface ChatResponse {
  reply: string;
  sessionId: string;
  corrections?: Corrections;
}

function App() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: '¡Hola! Soy tu compañero de conversación en español. ¡Vamos a practicar juntos!',
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-focus input after sending messages
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  // Focus input on component mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage.trim(),
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const requestBody: any = { message: userMessage.text };
      if (sessionId) {
        requestBody.sessionId = sessionId;
      }

      const response = await fetch('/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data: ChatResponse = await response.json();

      if (data.sessionId) {
        setSessionId(data.sessionId);
      }

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: data.reply,
        isUser: false,
        timestamp: new Date()
      };

      // Update the user message with corrections if available
      if (data.corrections) {
        console.log('Received corrections:', data.corrections); // Debug log
        setMessages(prev => {
          const newMessages = [...prev];
          // Find the last user message and add corrections to it
          for (let i = newMessages.length - 1; i >= 0; i--) {
            if (newMessages[i].isUser) {
              newMessages[i] = { ...newMessages[i], corrections: data.corrections };
              break;
            }
          }
          return [...newMessages, aiMessage];
        });
      } else {
        setMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Lo siento, hubo un error al conectar con el servidor. Por favor, inténtalo de nuevo.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearConversation = async () => {
    if (window.confirm('¿Estás seguro de que quieres borrar toda la conversación?')) {
      try {
        if (sessionId) {
          await fetch('/clear-conversation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId })
          });
        }

        setMessages([{
          id: Date.now().toString(),
          text: '¡Hola! Soy tu compañero de conversación en español. ¡Vamos a practicar juntos!',
          isUser: false,
          timestamp: new Date()
        }]);
        setSessionId(null);
      } catch (error) {
        console.error('Error clearing conversation:', error);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('sv-SE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };


  const CorrectionComponent = ({ message }: { message: Message }) => {
    if (!message.corrections || !message.isUser) return null;

    const { hasErrors, feedback } = message.corrections;

    if (!hasErrors) {
      return (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 flex items-center justify-end"
        >
          <div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
            <CheckCircleIcon className="w-4 h-4 text-green-500" />
            <span className="text-green-700 font-medium">Perfekt spanska! 🎉</span>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-2 flex justify-end"
      >
        <div className="bg-orange-50 border border-orange-200 rounded-lg overflow-hidden max-w-xs sm:max-w-md">
          <div className="px-3 py-2 flex items-center space-x-2 text-sm text-orange-700 bg-orange-100">
            <PencilIcon className="w-4 h-4 text-orange-500" />
            <span className="font-medium">
              {feedback.length} korrigering{feedback.length > 1 ? 'ar' : ''}
            </span>
          </div>
          
          <div className="border-t border-orange-200 bg-white">
            <div className="p-3 space-y-3">
              {feedback.map((correction, index) => (
                <div key={index} className="space-y-1">
                  <div className="flex items-start space-x-2 text-xs">
                    <span className="text-red-600 font-medium">Fel:</span>
                    <span className="text-red-700 line-through">{correction.mistake}</span>
                  </div>
                  <div className="flex items-start space-x-2 text-xs">
                    <span className="text-green-600 font-medium">Rätt:</span>
                    <span className="text-green-700 font-medium">{correction.correction}</span>
                  </div>
                  <div className="text-xs text-warm-gray-600 pl-4 border-l-2 border-orange-200">
                    {correction.explanation}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
      {/* Header */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 backdrop-blur-lg border-b border-white/20 shadow-soft"
      >
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-spanish-orange to-spanish-red rounded-xl">
              <LanguageIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gradient">Hablando</h1>
              <p className="text-sm text-warm-gray-600">Din spanska konversationspartner</p>
            </div>
          </div>
          
          <button
            onClick={handleClearConversation}
            className="btn-secondary flex items-center space-x-2 text-sm"
            title="Rensa konversation"
          >
            <TrashIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Rensa</span>
          </button>
        </div>
      </motion.header>

      {/* Chat Container */}
      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-4 py-6">
        {/* Messages */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full bg-white/50 backdrop-blur-sm rounded-2xl border border-white/30 shadow-elevated">
            <div className="h-full overflow-y-auto custom-scrollbar p-4 space-y-4">
              <AnimatePresence mode="popLayout">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className={`flex ${message.isUser ? 'justify-end' : 'justify-start'} mb-4`}
                  >
                    <div className="flex flex-col max-w-xs sm:max-w-md lg:max-w-lg">
                      <div className={`message-bubble ${message.isUser ? 'user-message' : 'ai-message'}`}>
                        {!message.isUser && (
                          <div className="flex items-center space-x-2 mb-2 opacity-70">
                            <SparklesIcon className="w-4 h-4" />
                            <span className="text-xs font-medium">Asistente</span>
                          </div>
                        )}
                        <p className="text-sm sm:text-base leading-relaxed">{message.text}</p>
                      </div>
                      <span className={`text-xs text-warm-gray-500 mt-1 ${
                        message.isUser ? 'text-right' : 'text-left'
                      }`}>
                        {formatTime(message.timestamp)}
                      </span>
                      
                      {/* Grammar Corrections */}
                      <CorrectionComponent message={message} />
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start mb-4"
                >
                  <div className="message-bubble ai-message">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-warm-gray-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-warm-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-warm-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                      <span className="text-sm text-warm-gray-600">Escribiendo...</span>
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

        {/* Input Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 mobile-safe-area"
        >
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl border border-white/30 shadow-elevated p-4">
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Escribe tu mensaje en español..."
                  className="chat-input min-h-[44px] max-h-32"
                  rows={1}
                  disabled={isLoading}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="btn-primary flex items-center justify-center min-w-[44px] h-11"
                title="Enviar mensaje"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Tips */}
          <div className="mt-4 flex items-center justify-center">
            <div className="flex items-center space-x-2 text-xs text-warm-gray-600 bg-white/60 backdrop-blur-sm rounded-full px-4 py-2">
              <ChatBubbleLeftRightIcon className="w-4 h-4" />
              <span>Tryck Enter för att skicka, Shift+Enter för ny rad</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default App;
